import { useEffect, useRef } from 'react';

/**
 * Click-outside-to-close hook for popovers, dropdowns, and floating
 * panels. Returns a ref — attach it to the element that should "contain"
 * legal clicks (typically the wrapper that includes both the trigger
 * button AND the popover panel). Any mousedown outside that subtree
 * fires `onOutside`.
 *
 * Why mousedown (not click):
 *   - Fires before focus/click events. If the user clicks another button,
 *     we close BEFORE that button's onClick — which lets the new button
 *     re-open its own popover cleanly without a "close-then-reopen" race.
 *   - Touch handlers also use mousedown semantics on iOS Safari.
 *
 * Usage:
 *   const ref = useClickOutside(open, () => setOpen(false));
 *   return <div ref={ref}><button>...</button>{open && <Panel/>}</div>;
 *
 * The `enabled` flag avoids registering listeners when the popover is
 * closed — saves a global mousedown listener per page-load.
 */
export function useClickOutside(enabled, onOutside) {
  const ref = useRef(null);

  useEffect(() => {
    if (!enabled) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onOutside();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [enabled, onOutside]);

  return ref;
}

export default useClickOutside;
